import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../components/ui/Card'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { TableWrap } from '../../components/ui/Table'
import { EmptyState } from '../../components/ui/EmptyState'
import { Button } from '../../components/ui/Button'
import { Avatar } from '../../components/ui/Avatar'
import { timeTrackingApi } from '../../api/timeTracking.api'
import { projectsApi } from '../../api/projects.api'
import { usersApi } from '../../api/users.api'
import styles from './TimeTrackingPage.module.css'

function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = Math.floor(totalSeconds % 60)
  if (h > 0) return `${h}h ${m}min`
  if (m > 0) return `${m}min ${s}s`
  return `${s}s`
}

export function TimeTrackingPage() {
  const [projectId, setProjectId] = useState('')
  const [userId, setUserId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)

  const projectsQuery = useQuery({ queryKey: ['projects', { pageSize: 100 }], queryFn: async () => (await projectsApi.list({ pageSize: 100 })).data })
  const usersQuery = useQuery({ queryKey: ['users', { pageSize: 100 }], queryFn: async () => (await usersApi.list({ pageSize: 100 })).data })

  const filterParams = { projectId: projectId || undefined, userId: userId || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }

  const summaryQuery = useQuery({
    queryKey: ['time-tracking', 'summary', filterParams],
    queryFn: () => timeTrackingApi.summary(filterParams),
  })
  const entriesQuery = useQuery({
    queryKey: ['time-tracking', 'list', filterParams, page],
    queryFn: () => timeTrackingApi.list({ ...filterParams, page, pageSize: 30 }),
  })

  const entries = entriesQuery.data?.data ?? []
  const total = entriesQuery.data?.meta.total ?? 0
  const summary = summaryQuery.data?.data
  const hasFilters = Boolean(projectId || userId || dateFrom || dateTo)

  const resetFilters = () => {
    setProjectId('')
    setUserId('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  return (
    <div>
      <PageHeader title="Zeiterfassung" subtitle="Wer hat wann wie lange gearbeitet — über alle Projekte hinweg." />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', alignItems: 'flex-end' }}>
        <Select style={{ maxWidth: 220 }} value={projectId} onChange={(e) => { setProjectId(e.target.value); setPage(1) }}>
          <option value="">Alle Projekte</option>
          {projectsQuery.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <Select style={{ maxWidth: 220 }} value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1) }}>
          <option value="">Alle Benutzer</option>
          {usersQuery.data?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </Select>
        <Input type="date" style={{ maxWidth: 160 }} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1) }} title="Von" />
        <Input type="date" style={{ maxWidth: 160 }} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1) }} title="Bis" />
        {hasFilters ? (
          <Button size="sm" variant="ghost" onClick={resetFilters}>
            Filter zurücksetzen
          </Button>
        ) : null}
      </div>

      {summary && summary.perUser.length > 0 ? (
        <div className={styles.summaryRow}>
          {summary.perUser.map(({ user, totalSeconds }) => (
            <div key={user.id} className={styles.summaryCard}>
              <Avatar name={user.displayName} imageUrl={user.avatarUrl} />
              <div>
                <div className={styles.summaryName}>{user.displayName}</div>
                <div className={styles.summaryTime}>{formatDuration(totalSeconds)}</div>
              </div>
            </div>
          ))}
          <div className={[styles.summaryCard, styles.summaryTotal].join(' ')}>
            <div>
              <div className={styles.summaryName}>Gesamt</div>
              <div className={styles.summaryTime}>{formatDuration(summary.totalSeconds)}</div>
            </div>
          </div>
        </div>
      ) : null}

      {entries.length === 0 && !entriesQuery.isLoading ? (
        <EmptyState mascot title="Keine Zeiteinträge" description="Für die aktuelle Filterung liegen keine Zeiterfassungs-Einträge vor." />
      ) : (
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Benutzer</th>
                <th>Projekt</th>
                <th>Aufgabe</th>
                <th>Dauer</th>
                <th>Quelle</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const projectName = entry.project?.name ?? entry.task?.project.name ?? '—'
                return (
                  <tr key={entry.id}>
                    <td style={{ whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                      {new Date(entry.startedAt).toLocaleString('de-DE')}
                    </td>
                    <td>{entry.user.displayName}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{projectName}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{entry.task ? entry.task.title : 'Allgemein'}</td>
                    <td>{entry.durationSeconds !== null ? formatDuration(entry.durationSeconds) : <Badge variant="info">läuft…</Badge>}</td>
                    <td>
                      <Badge variant={entry.source === 'MANUAL' ? 'neutral' : 'success'}>{entry.source === 'MANUAL' ? 'Manuell' : 'Timer'}</Badge>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </TableWrap>
      )}

      {total > 30 ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Zurück
          </Button>
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', alignSelf: 'center' }}>
            Seite {page} von {Math.ceil(total / 30)}
          </span>
          <Button size="sm" variant="ghost" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage((p) => p + 1)}>
            Weiter
          </Button>
        </div>
      ) : null}
    </div>
  )
}
